/** Longest edge sent to the model; larger photos are downscaled before upload. */
const MAX_UPLOAD_DIMENSION = 3072;
/** Stay under the API's 10 MB decoded-image limit with some headroom. */
const MAX_UPLOAD_BASE64_LENGTH = 12_000_000;
const PASSTHROUGH_TYPES = /^data:image\/(png|jpeg|webp);base64,/;

export interface PreparedImage {
  dataUrl: string;
  width: number;
  height: number;
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the selected image'));
    // Keeps the canvas untainted when stored images come from a separate API origin.
    if (!src.startsWith('data:')) img.crossOrigin = 'anonymous';
    img.src = src;
  });
}

/**
 * Turns any displayable image (uploaded data URL, SVG sample, stored render URL)
 * into a base64 PNG/JPEG/WEBP data URL the API accepts, downscaling oversized photos.
 */
export async function prepareImageForUpload(src: string): Promise<PreparedImage> {
  let img: HTMLImageElement;
  try {
    img = await loadImage(src);
  } catch (err) {
    // Formats the browser can't decode (e.g. HEIC outside Safari) go up untouched.
    if (/^data:image\/(heic|heif);base64,/.test(src)) return { dataUrl: src, width: 0, height: 0 };
    throw err;
  }

  const width = img.naturalWidth;
  const height = img.naturalHeight;
  const fits = Math.max(width, height) <= MAX_UPLOAD_DIMENSION && src.length <= MAX_UPLOAD_BASE64_LENGTH;
  if (PASSTHROUGH_TYPES.test(src) && fits) return { dataUrl: src, width, height };

  const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(width || 1, height || 1));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round((width || 1024) * scale));
  canvas.height = Math.max(1, Math.round((height || 768) * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available in this browser');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { dataUrl: canvas.toDataURL('image/jpeg', 0.92), width: canvas.width, height: canvas.height };
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}
